/**
 * Filter transformation utilities for converting between UI filter state,
 * URL parameters, and API-ready formats.
 *
 * Uses FiltersURLHelper internally for generic URL handling,
 * adds schema-aware transformations on top.
 */

import FiltersURLHelper from '~/components/entityViewer/entitiesDropDownFilter/FiltersURLHelper';

/**
 * Configuration for filter transformations.
 * @typedef {Object} FilterTransformConfig
 * @property {Object.<string, {start: string, end: string}>} [dateRangeKeys] - Map of date range filter keys to their API start/end keys
 * @property {string[]} [booleanKeys] - Keys that should be converted to booleans
 * @property {string[]} [numericArrayKeys] - Keys that contain arrays of numeric IDs
 * @property {string[]} [stringArrayKeys] - Keys that contain arrays of strings
 */

/**
 * Validates that a config object is provided.
 * @param {string} fnName - Function name for error message
 * @param {FilterTransformConfig} config - The config to validate
 * @throws {Error} If config is not provided
 */
function validateConfig(fnName, config) {
    if (!config) {
        throw new Error(`${fnName} requires a config object`);
    }
}

/**
 * Reconstructs UI-friendly filter state from raw URL-parsed JSON.
 * - Combines separate start/end date params into date range objects
 * - Ensures configured array keys are arrays
 *
 * @param {Object} rawJson - Raw JSON from URL parsing
 * @param {FilterTransformConfig} config - Configuration for transformations
 * @returns {Object} UI-friendly filter state
 */
function reconstructForUI(rawJson, config) {
    const result = { ...rawJson };

    // Reconstruct date ranges: deliveryDateStart + deliveryDateEnd → deliveryDateRange
    for (const [rangeKey, { start, end }] of Object.entries(config.dateRangeKeys || {})) {
        if (rawJson[start] !== undefined || rawJson[end] !== undefined) {
            result[rangeKey] = {
                start: rawJson[start] || '',
                end: rawJson[end] || '',
            };
            delete result[start];
            delete result[end];
        }
    }

    // Ensure configured array keys are arrays
    const arrayKeys = [...(config.numericArrayKeys || []), ...(config.stringArrayKeys || [])];
    for (const key of arrayKeys) {
        if (result[key] !== undefined && !Array.isArray(result[key])) {
            result[key] = [result[key]];
        }
    }

    return result;
}

/**
 * Prepares UI filter state for URL serialization.
 * - Expands date range objects into separate start/end params
 *
 * @param {Object} filters - UI filter state
 * @param {FilterTransformConfig} config - Configuration for transformations
 * @returns {Object} URL-ready filter object
 */
function prepareForUrl(filters, config) {
    const result = {};

    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') {
            continue;
        }

        // Expand date ranges: deliveryDateRange → deliveryDateStart + deliveryDateEnd
        if (config.dateRangeKeys?.[key]) {
            const { start: startKey, end: endKey } = config.dateRangeKeys[key];
            if (value && typeof value === 'object') {
                if (value.start) result[startKey] = value.start;
                if (value.end) result[endKey] = value.end;
            }
            continue;
        }

        // Pass through other values
        result[key] = value;
    }

    return result;
}

/**
 * Parses URL search params into UI filter state (schema-aware).
 * - Reconstructs date ranges from start/end parameters
 * - Ensures array keys are arrays
 *
 * @param {string} searchString - URL search string (e.g., window.location.search)
 * @param {FilterTransformConfig} config - Configuration for transformations (required)
 * @returns {Object} Parsed filter values for UI
 *
 * @example
 * const config = {
 *   dateRangeKeys: { deliveryDateRange: { start: 'deliveryDateStart', end: 'deliveryDateEnd' } },
 *   numericArrayKeys: ['vendorIds'],
 * };
 * const filters = fromUrl('?deliveryDateStart=2024-01-01&vendorIds=1&vendorIds=2', config);
 * // Result: { deliveryDateRange: { start: '2024-01-01', end: '' }, vendorIds: ['1', '2'] }
 */
export function fromUrl(searchString, config) {
    validateConfig('fromUrl', config);

    // Step 1: Generic URL → JSON (lodash-based, handles nested objects/arrays)
    const rawJson = FiltersURLHelper.fromSearchParams(searchString);

    // Step 2: Schema-aware reconstruction for UI
    return reconstructForUI(rawJson, config);
}

/**
 * Serializes UI filter state to URLSearchParams (schema-aware).
 * - Expands date ranges into start/end parameters
 *
 * @param {Object} filters - UI filter state
 * @param {FilterTransformConfig} config - Configuration for transformations (required)
 * @returns {URLSearchParams} URL search params object
 *
 * @example
 * const config = {
 *   dateRangeKeys: { deliveryDateRange: { start: 'deliveryDateStart', end: 'deliveryDateEnd' } },
 * };
 * const params = toUrl({ deliveryDateRange: { start: '2024-01-01', end: '2024-01-31' } }, config);
 * // Result: URLSearchParams with 'deliveryDateStart=2024-01-01&deliveryDateEnd=2024-01-31'
 */
export function toUrl(filters, config) {
    validateConfig('toUrl', config);

    const urlReady = prepareForUrl(filters, config);
    return FiltersURLHelper.toSearchParams(urlReady);
}

/**
 * Pushes UI filter state to browser URL (schema-aware).
 * Convenience function that combines toUrl with browser history API.
 *
 * @param {Object} filters - UI filter state
 * @param {FilterTransformConfig} config - Configuration for transformations (required)
 * @param {Object} [options={}] - Options for URL push
 * @param {boolean} [options.replace=false] - Whether to replace current history entry
 *
 * @example
 * pushToUrl({ vendorIds: [1, 2] }, config);
 * pushToUrl({ vendorIds: [1, 2] }, config, { replace: true });
 */
export function pushToUrl(filters, config, options = {}) {
    validateConfig('pushToUrl', config);

    const urlReady = prepareForUrl(filters, config);
    FiltersURLHelper.pushToBrowserUrl(urlReady, options);
}

/**
 * Transforms UI filter state to API-ready parameters (schema-aware).
 * - Splits date ranges into start/end parameters
 * - Converts boolean strings ('true'/'false'/'1'/'0') to actual booleans
 * - Converts string IDs to numbers for numeric array keys
 * - Removes empty arrays and undefined/null values
 *
 * @param {Object} filters - UI filter state
 * @param {FilterTransformConfig} config - Configuration for transformations (required)
 * @returns {Object} API-ready filter parameters
 *
 * @example
 * const config = {
 *   dateRangeKeys: { deliveryDateRange: { start: 'deliveryDateStart', end: 'deliveryDateEnd' } },
 *   booleanKeys: ['hasCrossDock'],
 *   numericArrayKeys: ['vendorIds'],
 * };
 * const apiFilters = toApi({
 *   deliveryDateRange: { start: '2024-01-01', end: '2024-01-31' },
 *   hasCrossDock: 'true',
 *   vendorIds: ['1', '2', '3'],
 * }, config);
 * // Result: { deliveryDateStart: '2024-01-01', deliveryDateEnd: '2024-01-31', hasCrossDock: true, vendorIds: [1, 2, 3] }
 */
export function toApi(filters, config) {
    validateConfig('toApi', config);

    const result = {};

    for (const [key, value] of Object.entries(filters)) {
        // Skip undefined, null, or empty string values
        if (value === undefined || value === null || value === '') {
            continue;
        }

        // Handle date range keys - split into start/end
        // Supports both { start, end } and { startDate, endDate } formats from different UI components
        if (config.dateRangeKeys?.[key]) {
            const { start: startKey, end: endKey } = config.dateRangeKeys[key];
            if (value && typeof value === 'object') {
                // Handle Date objects by converting to ISO string (date only)
                const formatDate = (d) => {
                    if (!d) return undefined;
                    if (d instanceof Date) {
                        return d.toISOString().split('T')[0]; // YYYY-MM-DD
                    }
                    return d; // Already a string
                };

                // Support both naming conventions: start/end and startDate/endDate
                const startValue = formatDate(value.start ?? value.startDate);
                const endValue = formatDate(value.end ?? value.endDate);

                if (startValue) result[startKey] = startValue;
                if (endValue) result[endKey] = endValue;
            }
            continue;
        }

        // Handle boolean keys - convert string to boolean
        // Accepts 'true'/'false' (from UI) or '1'/'0' (from URL serialization)
        if (config.booleanKeys?.includes(key)) {
            if (typeof value === 'string') {
                result[key] = value === 'true' || value === '1';
            } else if (typeof value === 'boolean') {
                result[key] = value;
            }
            continue;
        }

        // Handle numeric array keys - convert strings to numbers
        if (config.numericArrayKeys?.includes(key)) {
            if (Array.isArray(value)) {
                const numericArray = value
                    .map((v) => (typeof v === 'string' ? parseInt(v, 10) : v))
                    .filter((v) => typeof v === 'number' && !isNaN(v));
                if (numericArray.length > 0) {
                    result[key] = numericArray;
                }
            }
            continue;
        }

        // Handle string array keys - keep as is but filter empty
        if (config.stringArrayKeys?.includes(key)) {
            if (Array.isArray(value)) {
                const filteredArray = value.filter((v) => v !== undefined && v !== null && v !== '');
                if (filteredArray.length > 0) {
                    result[key] = filteredArray;
                }
            }
            continue;
        }

        // Handle regular arrays - skip if empty
        if (Array.isArray(value)) {
            if (value.length > 0) {
                result[key] = value;
            }
            continue;
        }

        // Pass through other values as-is
        result[key] = value;
    }

    return result;
}

// ============================================================================
// Utility Functions (Config-Agnostic)
// ============================================================================

/**
 * Removes empty/falsy filter values from an object.
 * Useful for cleaning up filter state before display or submission.
 *
 * @param {Object} filters - Filter values
 * @returns {Object} Filters with empty values removed
 */
export function removeEmptyFilters(filters) {
    const result = {};

    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') {
            continue;
        }

        if (Array.isArray(value) && value.length === 0) {
            continue;
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            const cleaned = removeEmptyFilters(value);
            if (Object.keys(cleaned).length > 0) {
                result[key] = cleaned;
            }
            continue;
        }

        result[key] = value;
    }

    return result;
}

/**
 * Checks if any filters are currently active (non-empty).
 *
 * @param {Object} filters - Filter values
 * @param {string[]} [excludeKeys=[]] - Keys to exclude from the check
 * @returns {boolean} True if any filters have values
 */
export function hasActiveFilters(filters, excludeKeys = []) {
    const cleaned = removeEmptyFilters(filters);
    const activeKeys = Object.keys(cleaned).filter((key) => !excludeKeys.includes(key));
    return activeKeys.length > 0;
}
